import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from '@/shared/ui/sonner';
import { AuthProvider, useAuth } from '@/shared/auth/AuthContext';
import RoleProtectedRoute, { defaultRouteForRole } from '@/shared/auth/RoleProtectedRoute';
import AppLayout from '@/app/AppLayout';
import LoginPage from '@/features/auth/pages/LoginPage';
import ForgotPasswordPage from '@/features/auth/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/features/auth/pages/ResetPasswordPage';
import ChangePasswordPage from '@/features/auth/pages/ChangePasswordPage';
import DashboardPage from '@/features/assets/pages/DashboardPage';
import MapPage from '@/features/assets/pages/MapPage';
import ClientsPage from '@/features/clients/pages/ClientsPage';
import ClientDetailPage from '@/features/clients/pages/ClientDetailPage';
import MandatPage from '@/features/clients/pages/MandatPage';
import MonitoringPage from '@/features/assets/pages/MonitoringPage';
import RattrapageMaterielPage from '@/features/assets/pages/RattrapageMaterielPage';
import AlertsPage from '@/features/alerts/pages/AlertsPage';
import InstallateurPage from '@/features/installer/pages/InstallateurPage';
import UsersPage from '@/features/admin/pages/UsersPage';
import MenusPage from '@/features/admin/pages/MenusPage';
import SAVPage from '@/features/distributeur/pages/SAVPage';
import OnboardingPage from '@/features/installer/pages/OnboardingPage';
import PilotagePage from '@/features/pilotage/pages/PilotagePage';
import AideLexiquePage from '@/features/aide/pages/AideLexiquePage';
import SymphonicsPage from '@/features/symphonics/pages/SymphonicsPage';
import SurvoltagePage from '@/features/survoltage/pages/SurvoltagePage';
import FacturationPage from '@/features/factures/pages/FacturationPage';
import AggregateursPage from '@/features/aggregateurs/pages/AggregateursPage';
import AgregateurRevenusPage from '@/features/aggregateurs/pages/AgregateurRevenusPage';
import BridgePage from '@/features/bridge/pages/BridgePage';
import DistributorClientDetail from '@/features/distributeur/pages/DistributorClientDetail';
import MandatTemplatePage from '@/features/mandats/pages/MandatTemplatePage';
import IdentitePage from '@/features/admin/pages/IdentitePage';
import RattrapageEnedisPage from '@/features/admin/pages/RattrapageEnedisPage';
import EnrollmentPage from '@/features/enrollment/pages/EnrollmentPage';
import SignatureMandatPage from '@/features/signature/pages/SignatureMandatPage';
import { OperatorProvider } from '@/shared/auth/OperatorContext';
import SuperAdminLayout from '@/app/SuperAdminLayout';
import ConsolePage from '@/features/superadmin/pages/ConsolePage';
import SousComptePage from '@/features/superadmin/pages/SousComptePage';
import JournalPage from '@/features/superadmin/pages/JournalPage';
import ComptesPlateformePage from '@/features/superadmin/pages/ComptesPlateformePage';
import TelemetriePage from '@/features/telemetrie/pages/TelemetriePage';
import ModuleRequis from '@/shared/auth/ModuleRequis';
import { estCftsign, cheminAutoriseSurCftsign } from '@/shared/marque';
import { CAP } from '@/shared/auth/capacites';

// Autorisation par rôle, pour les règles qui SONT une appartenance et non un droit :
// le super_admin de la console de plateforme, le parcours terrain de l'installateur et
// de son poseur, le SAV du distributeur.
//
// ⚠️ Pour tout le reste, c'est la CAPACITÉ qui garde l'écran (4e argument de `crm`).
// Une liste de rôles est une copie de la matrice du serveur : elle dérive, et elle
// dérive dans le sens rassurant. Ne rallonge pas ces listes pour ouvrir un écran —
// nomme la capacité que sa route exige.
const CRM = ['admin', 'commercial'];
const ADMIN_ONLY = ['admin'];
// Le poseur travaille sur ce meme ecran : sans lui dans cette liste, la garde le
// renvoyait vers sa page par defaut, qui EST cette page — boucle de redirection
// infinie, et un poseur incapable d'ouvrir l'application.
const INSTALLER = ['admin', 'installateur', 'poseur'];
const SAV = ['admin', 'distributeur'];
const PLATEFORME = ['super_admin'];

// Console de plateforme : layout et menu dédiés, super admin seul. Elle ne
// partage RIEN avec le layout métier, dont la barre latérale interroge
// /alerts/count toutes les minutes et ne lui rendrait que des 403.
const plateforme = (Page) => (
  <RoleProtectedRoute roles={PLATEFORME}><SuperAdminLayout><Page /></SuperAdminLayout></RoleProtectedRoute>
);

// Page CRM (avec sidebar) protégée par rôle et, quand la règle en est une, par CAPACITÉ.
//
// LOT I. Les écrans d'argent étaient gardés par `CRM = ['admin', 'commercial']`, une
// copie de la matrice du serveur. Le jour où le gestionnaire a perdu `tarifs:read`,
// cette copie ne l'a pas su : le menu ne proposait plus l'entrée, mais l'adresse tapée
// à la main ouvrait encore l'écran, qui restait vide. La capacité est lue là où elle
// est décidée, et la barre latérale filtre sur exactement la même.
const crm = (roles, Page, module = null, capacite = null) => (
  // `module` enveloppe l'ecran d'une explication quand le sous-compte ne l'a pas
  // souscrit. Sans lui, la page s'affichait et rendait « 0 resultat » : elle
  // traduisait un refus serveur en parc vide.
  <RoleProtectedRoute roles={roles} capacite={capacite}>
    <AppLayout><ModuleRequis module={module}><Page /></ModuleRequis></AppLayout>
  </RoleProtectedRoute>
);

/**
 * La porte CFTSign : sur son domaine, l'application de gestion n'existe pas.
 *
 * Le renvoi est SILENCIEUX, par choix : un poseur qui tape une adresse au hasard n'a
 * pas a lire un message d'erreur, il a a se retrouver sur son ecran.
 *
 * ⚠️ Le super administrateur en est EXEMPT, et ce n'est pas une faveur. Sa page par
 * defaut est `/superadmin` : le renvoyer vers `/installateur` le ferait rebondir entre
 * les deux redirections sans jamais s'arreter. Le cas s'est deja produit une fois sur
 * ce routeur, avec le poseur.
 */
function GardeDomaine({ children }) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  if (!estCftsign || user?.role === 'super_admin' || cheminAutoriseSurCftsign(pathname)) {
    return children;
  }
  return <Navigate to="/installateur" replace />;
}

function AppRoutes() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const defaultRoute = defaultRouteForRole(user?.role);

  return (
    <GardeDomaine>
    <Routes>
      <Route path="/login" element={user ? <Navigate to={defaultRoute} replace /> : <LoginPage />} />
      {/* Page publique : le client final n'a pas de compte. Aucune protection
          de role, aucune mise en page de CRM — seul le jeton de l'URL fait foi. */}
      <Route path="/enregistrement-produit/:token" element={<EnrollmentPage />} />
      {/* Page publique : le client signe son mandat a distance, sans compte. Hors
          `AppLayout` comme sa voisine — donc sans barre laterale, sans garde de role,
          et sans visite guidee : `TourGuide` est monte dans le layout et sort sans
          role. Seul le jeton de l'URL fait foi. */}
      <Route path="/signature-mandat/:jeton" element={<SignatureMandatPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage mode="reset" />} />
      <Route path="/set-password" element={<ResetPasswordPage mode="set" />} />
      <Route path="/changer-mot-de-passe" element={<RoleProtectedRoute><ChangePasswordPage /></RoleProtectedRoute>} />
      <Route path="/dashboard" element={crm(CRM, DashboardPage, null, CAP.DASHBOARD_READ)} />
      {/* La carte se remplit par `GET /api/installations`, garde par `dashboard:read`
          et non par `clients:read` : c'est la route qui decide, pas l'intuition. */}
      <Route path="/carte" element={crm(CRM, MapPage, 'CRM', CAP.DASHBOARD_READ)} />
      <Route path="/clients" element={crm(CRM, ClientsPage, 'CRM', CAP.CLIENTS_READ)} />
      <Route path="/clients/:id" element={crm(CRM, ClientDetailPage, 'CRM', CAP.CLIENTS_READ)} />
      <Route path="/clients/:id/mandat" element={crm(CRM, MandatPage, null, CAP.CLIENTS_READ)} />
      <Route path="/monitoring" element={crm(CRM, MonitoringPage, 'Monitoring', CAP.MONITORING_READ)} />
      <Route path="/alertes" element={crm(CRM, AlertsPage, null, CAP.MONITORING_READ)} />
      <Route path="/pilotage" element={crm(ADMIN_ONLY, PilotagePage, 'Pilotage', CAP.PILOTAGE)} />
      {/* Le rattrapage ATTRIBUE la production d'une machine a un client : c'est un acte
          d'exploitation, pas un acte de pose. Meme famille de droit que le bridge et le
          pilotage, et la garde reelle vit cote serveur (integrations:manage). */}
      <Route path="/rattrapage-materiel" element={crm(ADMIN_ONLY, RattrapageMaterielPage, 'Monitoring', CAP.INTEGRATIONS_MANAGE)} />
      <Route path="/utilisateurs" element={crm(ADMIN_ONLY, UsersPage, null, CAP.USERS_MANAGE)} />
      {/* L'ecran « Droits delegues » a ete DEMONTE le 03/09, apres recette par
          Boubacar : « ca n'est pas une page en plus, c'est un choix de privileges
          qu'on doit pouvoir faire directement dans la partie utilisateurs ». Le
          reglage vit desormais dans la fiche de l'utilisateur, sur /utilisateurs.

          LA ROUTE PART AVEC LA PAGE, et ce n'est pas du menage. Une route orpheline
          laisse l'ecran atteignable a l'adresse tapee a la main, alors que l'entree
          de menu a disparu : on aurait retire le chemin sans retirer la porte.
          Aucune redirection non plus — /droits-delegues n'a jamais ete un favori,
          l'ecran a vecu deux jours. Le filet <Route path="*"> renvoie a l'accueil. */}
      {/* `menus:write` et non `menus:read` : la LECTURE des menus sert les listes
          deroulantes de tout formulaire client, et les roles terrain l'ont. C'est
          l'EDITION qui reserve cet ecran. */}
      <Route path="/menus" element={crm(ADMIN_ONLY, MenusPage, null, CAP.MENUS_WRITE)} />
      {/* Pose des declarations NOMINATIVES chez Energrid, en serie : `bulk:execute`,
          comme toute action sur N objets d'un coup. */}
      <Route path="/rattrapage-enedis" element={crm(ADMIN_ONLY, RattrapageEnedisPage, null, CAP.BULK_EXECUTE)} />
      {/* L'ecran des tarifs generiques n'existe plus. Son bareme par type d'actif
          contredisait les deux modules par agregateur : deux sources de verite sur
          le meme chiffre. La version de reference a fait le meme choix et redirige
          aussi (lvdjgroup/flexcore-4-aout-, App.js ligne 109).

          TarifsPage.js a ete SUPPRIME le 24/08. Il vivait depuis en orphelin, sans
          aucun import : a chaque relecture il donnait l'impression d'un ecran a
          rebrancher, et coutait le temps de redecouvrir qu'il ne devait pas l'etre.

          La route, elle, RESTE : un favori vers /tarifs doit mener au successeur
          metier, pas a l'ecran d'accueil du role. Et le backend /api/admin/tarifs
          est conserve, il est teste et reference dans la carte des modules. */}
      <Route path="/tarifs" element={<Navigate to="/agregateurs" replace />} />
      {/* Le fil d Ariane des ecrans d agregateur pointait « /aggregateurs », deux g,
          quand la route s ecrit « /agregateurs ». Aucune route ne correspondait et le
          filet <Route path="*"> renvoyait au tableau de bord : depuis Survoltage, plus
          moyen de revenir choisir un autre agregateur. La faute est corrigee a la source,
          cette redirection rattrape les liens deja partages. */}
      <Route path="/aggregateurs" element={<Navigate to="/agregateurs" replace />} />
      <Route path="/aide" element={crm(CRM, AideLexiquePage)} />
      <Route path="/symphonics" element={crm(CRM, SymphonicsPage, 'Revenus', CAP.TARIFS_READ)} />
      <Route path="/survoltage" element={crm(CRM, SurvoltagePage, 'Revenus', CAP.TARIFS_READ)} />
      <Route path="/facturation" element={crm(CRM, FacturationPage, 'Revenus', CAP.TARIFS_READ)} />
      <Route path="/agregateurs" element={crm(CRM, AggregateursPage, null, CAP.TARIFS_READ)} />
      {/* La page generique d'un agregateur AJOUTE. Les deux integres gardent leurs
          ecrans propres — /symphonics et /survoltage — parce que leurs baremes ne se
          ramenent pas l'un a l'autre. La route serveur renvoie ici un 409 qui NOMME
          l'ecran a ouvrir, et la page y redirige. */}
      <Route path="/agregateurs/:cle" element={crm(CRM, AgregateurRevenusPage, null, CAP.TARIFS_READ)} />
      {/* Le bridge ouvre un canal de commande vers le parc : admin uniquement,
          comme le pilotage. La garde reelle vit cote serveur (integrations:manage). */}
      <Route path="/bridge" element={crm(ADMIN_ONLY, BridgePage, null, CAP.INTEGRATIONS_MANAGE)} />
      <Route path="/sav/chantier/:clientId" element={crm(SAV, DistributorClientDetail)} />
      <Route path="/sav/client/:clientId" element={crm(SAV, DistributorClientDetail)} />
      <Route path="/gabarits-mandat" element={crm(ADMIN_ONLY, MandatTemplatePage)} />
      <Route path="/identite" element={crm(ADMIN_ONLY, IdentitePage)} />
      <Route path="/superadmin" element={plateforme(ConsolePage)} />
      <Route path="/superadmin/sous-comptes/:id" element={plateforme(SousComptePage)} />
      <Route path="/superadmin/journal" element={plateforme(JournalPage)} />
      <Route path="/superadmin/comptes" element={plateforme(ComptesPlateformePage)} />
      {/* Chemin volontairement a la racine et non sous /superadmin : la telemetrie
          decrit la PLATEFORME, pas les sous-comptes. Elle reste gardee par le meme
          role, et rendue dans le meme layout. */}
      <Route path="/telemetrie" element={plateforme(TelemetriePage)} />
      {/* `SAV` (la liste de roles) et non `CAP.SAV_READ` : la route serveur est
          gardee par `require_role`, sans capacite. Voir Sidebar.js. */}
      <Route path="/sav" element={crm(SAV, SAVPage, 'SAV')} />
      {/* « Ajouter un produit », l'entrée de menu du SAV (01/10) : la même page, fiche ouverte. */}
      <Route path="/sav/produits/nouveau" element={crm(SAV, SAVPage, 'SAV')} />
      <Route path="/installateur" element={<RoleProtectedRoute roles={INSTALLER}><InstallateurPage /></RoleProtectedRoute>} />
      <Route path="/onboarding" element={<OnboardingPage />} />
      <Route path="*" element={<Navigate to={defaultRoute} replace />} />
    </Routes>
    </GardeDomaine>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
      <OperatorProvider>
        <AppRoutes />
        {/* Sans ce composant monte, les 22 fichiers qui appellent `toast.*` n'affichent
            rien : une suppression confirmee, une erreur de saisie ou un envoi reussi
            passaient tous en silence. Sa version le rend au meme endroit. */}
        <Toaster position="bottom-right" richColors closeButton />
      </OperatorProvider>
    </AuthProvider>
    </BrowserRouter>
  );
}
